/**
 * Indicador de progreso de una traza en curso (status pending / running).
 *
 * El backend consulta a riga-traza-v2 cada pocos segundos y copia en
 * `traza_job` los contadores `done_items` / `total_items` / `error_items`;
 * el front los lee con polling de 1 s (`useTrazaJobByReferencia`). Si riga
 * informa el avance de forma incremental, la barra avanza; si los contadores
 * todavía no están (recién enviada, o riga los publica solo al final), se
 * muestra una barra indeterminada.
 */

import { Loader2 } from 'lucide-react';
import type { TrazaJobDetalle } from '@/api/contracts/traza-job-detalle';

interface Props {
  /** Registro del job con datos frescos del polling. Null si todavía no llegó. */
  traza: TrazaJobDetalle | null;
}

export function TrazaProgreso({ traza }: Props) {
  const total = traza?.totalItems ?? null;
  const done = traza?.doneItems ?? 0;
  const errores = traza?.errorItems ?? 0;
  const determinado = total != null && total > 0;
  const porcentaje = determinado ? Math.min(100, Math.round((done / total) * 100)) : null;
  const etapa = traza?.status === 'running' ? 'Informando al SNT' : 'En cola';

  return (
    <div
      role="status"
      aria-live="polite"
      className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-xs text-orange-900"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-everex-naranja" />
          <span>
            <b>Trazando…</b> · {etapa}
            {traza?.jobId != null && (
              <span className="text-orange-700"> · proceso #{traza.jobId}</span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {determinado && (
            <span className="font-mono">
              {done} de {total} unidades informadas
            </span>
          )}
          {errores > 0 && <span className="badge badge-yellow">{errores} con error</span>}
        </div>
      </div>

      <div
        className="mt-2 h-2 w-full overflow-hidden rounded-full bg-orange-100"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={determinado ? total : undefined}
        aria-valuenow={determinado ? done : undefined}
      >
        {determinado ? (
          <div
            className="h-full rounded-full bg-everex-naranja transition-[width] duration-500"
            style={{ width: `${porcentaje ?? 0}%` }}
          />
        ) : (
          <div className="global-progress-bar h-full w-full bg-everex-naranja" />
        )}
      </div>

      <p className="mt-1.5 text-[11px] text-orange-800">
        El estado se actualiza automáticamente; no es necesario recargar.
      </p>
    </div>
  );
}
